import { getAuthorizationHeader } from "@/api";

import { configureClientActivity, reportClientActivity } from "./clientActivity";
import { sendClientActivity } from "./sendClientActivity";
import { type CardActivity, type CardTapActivity, ClientActivityType } from "./types";

const DELIVERY_ID_PROPERTY = "deliveryId";

let configuredPlatformId: string | undefined;
let isSuppressed = false;
let signInReportedForUserId: string | undefined;

// An admin impersonating a patient is not the patient using the app.
const isReportingAuthenticated = () => !isSuppressed && getAuthorizationHeader() !== undefined;

export function ensureClientActivityConfigured(platformId: string | undefined): void {
  if (platformId === undefined || platformId === configuredPlatformId) return;
  configureClientActivity({
    send: sendClientActivity,
    platformId,
    isAuthenticated: isReportingAuthenticated,
  });
  configuredPlatformId = platformId;
}

function report(type: ClientActivityType, details?: Parameters<typeof reportClientActivity>[1]) {
  if (configuredPlatformId === undefined || isSuppressed) return;
  reportClientActivity(type, details);
}

export function startClientActivity({
  platformId,
  isAdminImpersonation,
}: {
  platformId: string | undefined;
  isAdminImpersonation: boolean;
}): void {
  ensureClientActivityConfigured(platformId);
  isSuppressed = isAdminImpersonation;
  report(ClientActivityType.AppOpen);
}

export function reportSignIn(userId: string): void {
  if (signInReportedForUserId === userId) return;
  signInReportedForUserId = userId;
  report(ClientActivityType.SignIn);
}

export function reportPageView(route: string, properties?: Record<string, string>): void {
  report(ClientActivityType.PageView, properties === undefined ? { route } : { route, properties });
}

export function reportProductView(route: string, properties: Record<string, string>): void {
  report(ClientActivityType.ProductView, { route, properties });
}

export function reportCartChange(
  type: typeof ClientActivityType.CartAdd | typeof ClientActivityType.CartRemove,
  properties: Record<string, string>,
): void {
  report(type, { properties });
}

const NAME_PROPERTY = "name";

/** The card's title is omitted when it has none, as the cart changes do, rather than sent blank. */
function cardProperties({ name, ...rest }: CardActivity | CardTapActivity): Record<string, string> {
  return name ? { ...rest, [NAME_PROPERTY]: name } : rest;
}

export function reportCardView(card: CardActivity): void {
  report(ClientActivityType.CardView, { properties: cardProperties(card) });
}

export function reportCardTap(card: CardTapActivity): void {
  report(ClientActivityType.CardTap, { properties: cardProperties(card) });
}

export function reportPushOpen(deliveryId: string | undefined): void {
  report(
    ClientActivityType.PushOpen,
    deliveryId === undefined ? {} : { properties: { [DELIVERY_ID_PROPERTY]: deliveryId } },
  );
}
